<template>
  <div class="cover-block wrap">
    <div class="cover-block__breadcrumbs">
      <SkeletonBlock
        :is-active="isSkeletonActive"
        :width="128"
        :height="20"
      >
        <BreadCrumbs
          v-if="!isSkeletonActive"
          :breadcrumbs
          :is-full-breadcrumbs="fullBreadcrumbsIsShown"
        />
      </SkeletonBlock>
    </div>

    <Text
      as="h1"
      :text="seoTitle"
      :skeleton="isSkeletonActive"
      :skeleton-breakpoints="{ default: '680px', tablet: '360px', mobile: '320px' }"
      class="cover-block__title"
      data-test="listing-name"
    />

    <Text
      as="span"
      :text="seoSubtitle"
      :skeleton="isSkeletonActive"
      :skeleton-breakpoints="'308px'"
      class="cover-block__subtitle"
    />
  </div>
</template>

<script setup lang="ts">
  import { computed, nextTick, ref, watch } from 'vue';
  import { useRoute } from 'vue-router';
  import { useHead } from '@unhead/vue';
  import { SkeletonBlock } from '@experience/core-skeleton-lib';
  import { BreadCrumbs, generateTourBreadcrumbs, type GenerateBreadcrumbsParams } from '@/entities/breadCrumbs';
  import { useTourCitySeoInfo } from '@/entities/city/api/useTourCitySeoInfo.ts';
  import { useTourCityTagSeoInfo } from '@/entities/city/api/useTourCityTagSeoInfo.ts';
  import { LocationType } from '@/entities/location';
  import { createRubricTitlesMap } from '@/entities/rubrics/utils/create-rubric-titles-map.ts';
  import type { CityInfo, CityTag, CountryInfo, RegionInfo } from '@/shared/api/experience';
  import { useListingSkeleton } from '@/shared/composables/useDelayedListingSkeleton.ts';
  import { useLoaderStore } from '@/shared/store/useLoaderStore';
  import { useSeoStore } from '@/shared/store/useSeoStore.ts';
  import { Text } from '@/shared/ui/text';

  interface Props {
    currentTag: string;
    slug: string;
    geoObject: CityInfo | CountryInfo | RegionInfo;
    tags: CityTag[];
    rubric?: string | null;
  }

  const props = defineProps<Props>();

  const route = useRoute();
  const loaderStore = useLoaderStore();
  const seoStore = useSeoStore();

  const { isSkeletonActive } = useListingSkeleton();
  const cityInfo = computed(() => props.geoObject as CityInfo);
  const isAllTag = computed(() => props.currentTag === 'all' || props.currentTag === '');

  const {
    tourCitySeoData,
    isSuccess: isTourCitySeoLoaded,
    isFetching: isTourCitySeoFetching,
  } = useTourCitySeoInfo(
    computed(() => props.slug),
    isAllTag,
  );

  const {
    cityTagSeoData,
    isSuccess: isCityTagSeoLoaded,
    isFetching: isCityTagSeoFetching,
  } = useTourCityTagSeoInfo(
    computed(() => props.slug),
    computed(() => props.currentTag),
    computed(() => !isAllTag.value),
  );

  const breadcrumbs = computed(() => {
    const params: GenerateBreadcrumbsParams = {
      currentTag: props.currentTag,
      geoObject: cityInfo.value,
      rubric: props.rubric ?? null,
      rubricTitlesMap: createRubricTitlesMap(props.tags),
      isExperienceDetailsPage: false,
      locationType: LocationType.CITY,
      citySlug: props.slug,
      cityName: cityInfo.value.name_ru,
      country: cityInfo.value.country?.slug,
      countryName: cityInfo.value.country?.name_ru,
    };

    return generateTourBreadcrumbs(params);
  });

  const fullBreadcrumbsIsShown = computed(() => props.currentTag !== 'all' && Boolean(props.currentTag));
  const seoData = computed(() => (isAllTag.value ? tourCitySeoData.value?.seo : cityTagSeoData.value?.seo));
  const seoTitle = computed(() => seoData.value?.h1 ?? '');
  const seoSubtitle = computed(() => seoData.value?.h2 ?? '');
  const headTitle = computed(() => seoData.value?.title ?? '');
  const description = computed(() => seoData.value?.description ?? '');
  const canonicalLink = computed(() => `${window.location.origin}${route.path}`);
  const seoLoadStartTime = ref<number | null>(null);

  const seoIsLoaded = computed(() => {
    if (isAllTag.value) {
      return Boolean(isTourCitySeoLoaded.value);
    }

    return Boolean(isCityTagSeoLoaded.value);
  });

  const seoIsFetching = computed(() => {
    if (isAllTag.value) {
      return Boolean(isTourCitySeoFetching.value);
    }

    return Boolean(isCityTagSeoFetching.value);
  });

  const setSeoReady = () => {
    loaderStore.ready.seo = true;
    seoLoadStartTime.value = null;
  };

  const handleSeoLoaded = () => {
    if (seoLoadStartTime.value !== null) {
      const elapsed = Date.now() - seoLoadStartTime.value;
      const minDisplayTime = 800;
      const remainingTime = Math.max(0, minDisplayTime - elapsed);

      if (elapsed < 50) {
        setSeoReady();
      } else if (remainingTime > 0) {
        setTimeout(setSeoReady, remainingTime);
      } else {
        setSeoReady();
      }

      return;
    }

    setSeoReady();
  };

  watch(
    () => props.currentTag,
    (newTag, oldTag) => {
      if (oldTag !== undefined && newTag !== oldTag) {
        loaderStore.ready.seo = false;
        seoLoadStartTime.value = Date.now();

        nextTick(() => {
          if (seoIsLoaded.value && !seoIsFetching.value && !loaderStore.ready.seo) {
            handleSeoLoaded();
          }
        });
      }
    },
  );

  watch(
    () => [seoIsLoaded.value, seoIsFetching.value],
    ([isLoaded, isFetching]) => {
      if (isFetching && !isLoaded) {
        if (loaderStore.ready.seo) {
          loaderStore.ready.seo = false;
        }

        return;
      }

      if (isLoaded && !isFetching && !loaderStore.ready.seo) {
        handleSeoLoaded();
      }
    },
    { immediate: true },
  );

  watch(
    () => headTitle.value,
    (value) => (document.title = value),
    { immediate: true },
  );

  useHead({
    title: () => headTitle.value,
    link: [
      {
        rel: 'canonical',
        href: () => canonicalLink.value,
      },
    ],
    meta: () => {
      const metaArr = [
        {
          name: 'description',
          content: () => description.value,
        },
        {
          property: 'og:title',
          content: () => headTitle.value,
        },
        {
          property: 'og:description',
          content: () => description.value,
        },
        {
          content: () => seoStore.resizeImage,
          property: 'og:image',
        },
        {
          property: 'og:url',
          content: () => canonicalLink.value,
        },
        {
          name: 'twitter:card',
          content: 'summary_large_image',
        },
        {
          name: 'twitter:image',
          content: () => seoStore.resizeImage,
        },
      ];

      if (seoData.value?.noindex) {
        metaArr.push({
          name: 'robots',
          content: 'noindex, nofollow',
        });
      }

      return metaArr;
    },
  });
</script>

<style lang="scss" scoped>
  @use '@experience/design-tokens/typography' as typography;
  @use '@experience/shared/dist/assets/adaptivity' as adaptivity;

  .cover-block__title {
    margin-top: 8px;
    margin-bottom: 4px;
    text-wrap: balance;

    @include typography.base-1000-950-extra;

    @include adaptivity.tablet {
      @include typography.base-700-500-extra;
    }
  }

  .cover-block__subtitle {
    display: block;
    overflow: hidden;
    margin-top: 8px;
    margin-bottom: 4px;
    text-overflow: ellipsis;
    hyphens: auto;
    -webkit-box-orient: vertical;
    -webkit-line-clamp: 3;

    @include typography.base-300-300-medium;
  }
</style>
